import React, { useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { logout, checkAuthStatus } from '../store/slices/authSlice';
import BrandLogo from './brand/BrandLogo';
import LanguageSwitcher from './brand/LanguageSwitcher';
import DappMobileNav from './nav/DappMobileNav';
import { useI18n } from '../i18n';

export default function DappLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user, token, loading } = useSelector((state) => state.auth);
  const hydrated = useRef(false);
  const { t } = useI18n();

  useEffect(() => {
    if (hydrated.current) return;
    if (token && !user && !loading) {
      hydrated.current = true;
      dispatch(checkAuthStatus());
    }
  }, [token, user, loading, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const tabValue = (() => {
    const p = location.pathname;
    if (p.startsWith('/transfer')) return '/transfer';
    if (p.startsWith('/profile')) return '/profile';
    if (p.startsWith('/admin')) return '/admin';
    return false;
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', pb: { xs: 7, md: 0 } }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'primary.main', borderBottom: 1, borderColor: 'primary.dark' }}>
        <Toolbar sx={{ gap: 1, flexWrap: 'wrap', py: 0.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <BrandLogo variant="app" onClick={() => navigate('/transfer')} />
          </Box>
          <LanguageSwitcher />
          {!isMobile && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <Button color="inherit" size="small" onClick={() => navigate('/')}>
                {t('nav.intro')}
              </Button>
              <Button color="inherit" size="small" onClick={() => navigate('/guide')}>
                {t('nav.help')}
              </Button>
              {isAuthenticated ? (
                <>
                  <Button color="inherit" size="small" onClick={() => navigate('/profile')}>
                    {t('nav.profile')}
                  </Button>
                  <Button color="inherit" size="small" onClick={handleLogout}>
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <Button color="inherit" size="small" onClick={() => navigate('/auth')}>
                  {t('nav.login')}
                </Button>
              )}
            </Stack>
          )}
        </Toolbar>
        {!isMobile && (
          <Tabs
            value={tabValue || '/transfer'}
            onChange={(_, v) => navigate(v)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ px: 2, minHeight: 44, '& .MuiTab-root': { minHeight: 44, fontWeight: 600 } }}
          >
            <Tab label={t('nav.transfer')} value="/transfer" />
            <Tab label={t('nav.profile')} value="/profile" disabled={!isAuthenticated} />
          </Tabs>
        )}
      </AppBar>

      <Container component="main" maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 2, md: 3 } }}>
        <Outlet />
      </Container>

      <DappMobileNav />

      <Box component="footer" sx={{ py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
        <Container maxWidth="lg">
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            {t('footer.dapp', { year: new Date().getFullYear() })}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
